"use client"

import { useState, ReactNode, PropsWithoutRef, JSX } from "react"
import { FormProvider, useForm, UseFormProps, UseFormReturn } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Group, Alert, Button } from "@mantine/core"
import { z } from "zod"
import { Value } from "@stringtale/react"

export interface FormProps<S extends z.ZodType<any, any>>
  extends Omit<PropsWithoutRef<JSX.IntrinsicElements["form"]>, "onSubmit"> {
  /** All your form fields */
  children?: ReactNode
  /** Text to display in the submit button */
  submitText?: ReactNode
  schema?: S
  onSubmit: (
    values: z.infer<S>,
    ctx: UseFormReturn<z.TypeOf<S>, object>
  ) => Promise<void | OnSubmitResult>
  initialValues?: UseFormProps<z.infer<S>>["defaultValues"]
}

interface OnSubmitResult {
  FORM_ERROR?: ReactNode
  [prop: string]: any
}

export const FORM_ERROR = "FORM_ERROR"

export function Form<S extends z.ZodType<any, any>>({
  children,
  submitText,
  schema,
  initialValues,
  onSubmit,
  ...props
}: FormProps<S>) {
  const ctx = useForm<z.infer<S>>({
    mode: "onBlur",
    resolver: schema ? zodResolver(schema) : undefined,
    defaultValues: initialValues,
  })
  const [formError, setFormError] = useState<ReactNode | null>(null)
  // const { classes } = useStyles()
  return (
    <FormProvider {...ctx}>
      <form
        onSubmit={ctx.handleSubmit(async (values) => {
          try {
            const result = (await onSubmit(values, ctx)) || {}
            for (const [key, value] of Object.entries(result)) {
              if (key === FORM_ERROR) {
                setFormError(value)
              } else {
                ctx.setError(key as any, {
                  type: "submit",
                  message: value,
                })
              }
            }
          } catch (error) {
            console.log("error", error)
          }
        }, console.log)}
        className="flex flex-col gap-4"
        {...props}
      >
        {/* Form fields supplied as children are rendered here */}
        {children}

        {formError && (
          <Alert
            title={<Value name="apps.app.src.core.components.form.alert">Alert</Value>}
            color="red"
          >
            {formError}
          </Alert>
        )}

        {submitText && (
          <Group>
            <Button type="submit" disabled={ctx.formState.isSubmitting}>
              {submitText}
            </Button>
          </Group>
        )}
      </form>
    </FormProvider>
  )
}

export default Form
